<template>
  <div>
    <AccountSettingsSecuritySettings v-if="$auth.user && $auth.user.securitySection.show" />
    <template v-if="isFlutterReady">
      <div class="text-h2 mt-6 mb-4">
        {{ $t('profile.appSecurity') }}
      </div>
      <MobileButtonGroup :buttons="buttons" />
    </template>
    <template v-else-if="isNative && !biometryCheckBusy">
      <div class="text-h2 mt-6 mb-4">
        {{ $t('profile.cAppSecurity.title') }}
      </div>

      <template v-if="isBiometryAvailable">
        <v-row no-gutters align="center" class="py-0 py-md-4">
          <v-col class="d-flex flex-column">
            <div class="text-h3">
              {{ $t('profile.cAppSecurity.biometry.title') }}
            </div>
            <div class="text-body-small grey-darken-1--text">
              {{ $t('profile.cAppSecurity.biometry.description') }}
            </div>
          </v-col>
          <v-col class="flex-shrink-1 flex-grow-0 ml-4">
            <v-switch
              v-model="biometrySwitchVal"
              hide-details
              inset
              color="success"
              :loading="biometryAuthBusy"
              :disabled="biometryAuthBusy"
              @click="sendGtmEvent('Přepnutí biometrického zámku')"
              @change="toggleBiometry"
            />
          </v-col>
        </v-row>
      </template>

      <template v-if="!biometryEnabled">
        <v-row no-gutters align="center" class="py-0 py-md-4">
          <v-col class="d-flex flex-column">
            <div class="text-h3">
              {{ $t('profile.cAppSecurity.pin.title') }}
            </div>
            <div class="text-body-small grey-darken-1--text">
              {{ $t('profile.cAppSecurity.pin.description') }}
            </div>
          </v-col>
          <v-col class="flex-shrink-1 flex-grow-0 ml-4">
            <v-switch
              v-model="pinSwitchVal"
              hide-details
              inset
              color="success"
              :loading="pinBusy"
              :disabled="pinBusy"
              @change="togglePin"
            />
          </v-col>
        </v-row>
      </template>
    </template>

    <PinSetupDialog v-model="showPinSetup" @confirm="onPinConfirmed" @input="onPinSetupInput" />
    <PinVerifyDialog
      v-model="showPinVerify"
      :show-pad="pinEnabled"
      :error="pinVerifyError"
      :busy="pinVerifyBusy"
      @confirm="onPinVerified"
      @input="onPinVerifyInput"
    />
  </div>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex';
import AccountSettingsAccountSettingsSecuritySettings from '@/components/Molecules/AccountSettings/AccountSettingsSecuritySettings.vue';
import { Capacitor } from '@capacitor/core';
import { useAppLock } from '@/feature/app-lock/composables/useAppLock';
import { useCapacitorBiometry } from '@/composables/capacitor/useCapacitorBiometry';
import PinSetupDialog from '@/feature/app-lock/components/PinSetupDialog.vue';
import PinVerifyDialog from '@/feature/app-lock/components/PinVerifyDialog.vue';

export default {
  name: 'SecuritySettings',
  components: {
    AccountSettingsSecuritySettings: AccountSettingsAccountSettingsSecuritySettings,
    PinSetupDialog,
    PinVerifyDialog
  },
  data() {
    return {
      biometryCheckBusy: true,
      biometryAuthBusy: false,
      pinBusy: false,
      isBiometryAvailable: false,
      showPinSetup: false,
      showPinVerify: false,
      pinVerifyError: null,
      pinVerifyBusy: false,
      pendingAction: null,
      pinSwitchVal: false,
      biometrySwitchVal: false
    };
  },
  computed: {
    ...mapGetters('appLock', ['biometryEnabled', 'pinEnabled']),
    isNative() {
      return Capacitor.isNativePlatform();
    },
    buttons() {
      return [
        {
          img: 'lock',
          text: this.$t('profile.applicationLock'),
          click: () => {
            if (this.isFlutterReady) {
              window.flutter_inappwebview.callHandler('openPinSettings');
            }
          }
        }
      ];
    },
    isFlutter() {
      return !!window.flutter_inappwebview;
    }
  },
  watch: {
    pinEnabled(val) {
      this.pinSwitchVal = val;
    },
    biometryEnabled(val) {
      this.biometrySwitchVal = val;
    }
  },
  async mounted() {
    if (Capacitor.isNativePlatform()) {
      try {
        const { checkBiometry } = useCapacitorBiometry();
        const { loadState } = useAppLock();

        const [biometryCheck, { biometryEnabled, pinEnabled }] = await Promise.all([
          checkBiometry(),
          loadState()
        ]);

        this.isBiometryAvailable = biometryCheck.isAvailable;
        this.$store.commit('appLock/setBiometryEnabled', biometryEnabled);
        this.$store.commit('appLock/setPinEnabled', pinEnabled);
        this.biometrySwitchVal = biometryEnabled;
        this.pinSwitchVal = pinEnabled;
      } catch (error) {
        this.handleError({ error, code: '0x00000009' });
      }
    }

    this.biometryCheckBusy = false;
  },
  methods: {
    ...mapMutations('appLock', ['setBiometryEnabled', 'setPinEnabled']),
    async toggleBiometry(newValue) {
      if (this.biometryAuthBusy) {
        return;
      }

      if (newValue && this.pinEnabled) {
        this.pendingAction = 'enableBiometry';
        this.pinVerifyError = null;
        this.showPinVerify = true;
        return;
      }

      this.biometryAuthBusy = true;
      try {
        const { enableBiometry, disableBiometry } = useAppLock();
        if (newValue) {
          await enableBiometry();
          this.setBiometryEnabled(true);
          this.setPinEnabled(false);
        } else {
          await disableBiometry();
          this.setBiometryEnabled(false);
        }
      } catch (error) {
        this.biometrySwitchVal = this.biometryEnabled;
        this.handleError({ error, code: '0x0000004E' });
      } finally {
        this.biometryAuthBusy = false;
      }
    },
    togglePin(newValue) {
      if (newValue) {
        this.showPinSetup = true;
      } else {
        this.pendingAction = 'disablePin';
        this.pinVerifyError = null;
        this.showPinVerify = true;
      }
    },
    onPinSetupInput(open) {
      if (!open) {
        this.pinSwitchVal = this.pinEnabled;
      }
    },
    async onPinConfirmed(pin) {
      this.showPinSetup = false;
      this.pinBusy = true;
      try {
        const { setPin } = useAppLock();
        await setPin(pin);
        this.setPinEnabled(true);
      } catch (error) {
        this.handleError({ error, code: '0x0000004F' });
      } finally {
        this.pinBusy = false;
      }
    },
    onPinVerifyInput(open) {
      if (!open) {
        if (this.pendingAction === 'disablePin') {
          this.pinSwitchVal = this.pinEnabled;
        }
        if (this.pendingAction === 'enableBiometry') {
          this.biometrySwitchVal = this.biometryEnabled;
        }
        this.pendingAction = null;
        this.pinVerifyError = null;
      }
    },
    async onPinVerified(pin) {
      this.pinVerifyBusy = true;
      try {
        const { verifyPin, removePin, enableBiometry } = useAppLock();
        const correct = await verifyPin(pin);
        if (!correct) {
          this.pinVerifyError = this.$t('profile.cAppSecurity.pin.verify.incorrect');
          return;
        }

        this.showPinVerify = false;
        const action = this.pendingAction;
        this.pendingAction = null;

        if (action === 'disablePin') {
          this.pinBusy = true;
          try {
            await removePin();
            this.setPinEnabled(false);
          } catch (error) {
            this.handleError({ error, code: '0x00000050' });
          } finally {
            this.pinBusy = false;
          }
        } else if (action === 'enableBiometry') {
          this.biometryAuthBusy = true;
          try {
            await enableBiometry();
            this.setBiometryEnabled(true);
            this.setPinEnabled(false);
          } catch (error) {
            this.biometrySwitchVal = this.biometryEnabled;
            this.handleError({ error, code: '0x0000004E' });
          } finally {
            this.biometryAuthBusy = false;
          }
        }
      } catch (error) {
        this.handleError({ error, code: '0x00000051' });
      } finally {
        this.pinVerifyBusy = false;
      }
    }
  }
};
</script>
